import {
  HHAlert,
  HHBox,
  HHStack,
  HHToggleButton,
  HHToggleButtonGroup,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Skeleton } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackLink } from '../components/back-link';
import {
  ConfigurationForm,
  ConfigurationFormValues,
} from '../components/configuration-form';
import { ConfigurationHeader } from '../components/configuration-header';
import { ConfigurationJsonEditForm } from '../components/configuration-json-edit-form';
import { ConfigurationLiveEditConfirmationDialog } from '../components/configuration-live-edit-confirmation-dialog';
import { ConfigurationStatusActions } from '../components/configuration-status-actions';
import { ConfigurationStatusConfirmationDialog } from '../components/configuration-status-confirmation-dialog';
import { SurfaceCard } from '../components/surface-card';
import {
  CONFIGURATIONS_BACK_LINK_LABEL,
  PARTNER_DATA_GATEWAY_ROUTES,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { usePermissions } from '../context/permissions-context';
import { ConfigurationStatus } from '../domain/enums';
import { CreateConfigurationInput } from '../domain/types';
import { useActionsCatalog } from '../hooks/use-actions-catalog';
import { useConfiguration } from '../hooks/use-configuration';
import { useUpdateConfiguration } from '../hooks/use-configuration-mutations';
import { useConfigurationStatusTransition } from '../hooks/use-configuration-status-transition';
import { useDataProviders } from '../hooks/use-data-providers';
import { toConfigurationFile } from '../utils/configuration-paste.utils';
import { stringifyJson } from '../utils/json.utils';

export const CONFIGURATION_DETAIL_PAGE_TEST_ID =
  'partner-data-gateway-configuration-detail-page';
export const CONFIGURATION_DETAIL_NO_ACCESS_TEST_ID =
  'partner-data-gateway-configuration-detail-no-access';
export const CONFIGURATION_DETAIL_LOADING_TEST_ID =
  'partner-data-gateway-configuration-detail-loading';
export const CONFIGURATION_DETAIL_ERROR_TEST_ID =
  'partner-data-gateway-configuration-detail-error';
export const CONFIGURATION_DETAIL_BACK_LINK_TEST_ID =
  'partner-data-gateway-configuration-detail-back-link';
export const CONFIGURATION_DETAIL_SAVED_TEST_ID =
  'partner-data-gateway-configuration-detail-saved-alert';
export const CONFIGURATION_DETAIL_TERMINAL_TEST_ID =
  'partner-data-gateway-configuration-detail-terminal-note';
export const CONFIGURATION_DETAIL_MODE_TEST_ID =
  'partner-data-gateway-configuration-detail-mode';
export const CONFIGURATION_DETAIL_MODE_LABEL = 'How to edit';
export const CONFIGURATION_DETAIL_MODE_FORM_LABEL = 'Form';
export const CONFIGURATION_DETAIL_MODE_JSON_LABEL = 'Edit JSON';
export const CONFIGURATION_DETAIL_UNSAVED_JSON_TEST_ID =
  'partner-data-gateway-configuration-detail-unsaved-json-note';
export const CONFIGURATION_DETAIL_UNSAVED_JSON_MESSAGE =
  'Save or discard your JSON changes to switch back to the form.';

type EditMode = 'form' | 'json';

interface PendingLiveEdit {
  values: CreateConfigurationInput;
  changedFields: (keyof CreateConfigurationInput)[];
  /**
   * Set once the operator confirms. The mutation hook keeps its last error
   * until the next save, so without this the dialog would open showing a
   * failure from an earlier attempt.
   */
  attempted: boolean;
}

export const CONFIGURATION_DETAIL_READ_ONLY_TEST_ID =
  'partner-data-gateway-configuration-detail-read-only-note';

export const CONFIGURATION_DETAIL_TERMINAL_MESSAGE =
  'Deleted configurations are read-only — create a new one to replace it.';
export const CONFIGURATION_DETAIL_READ_ONLY_MESSAGE =
  'You have read-only access to configurations.';

/**
 * Detail / edit view for a single configuration.
 *
 * Editing and the status transitions sit on the same page deliberately:
 * publishing a rule the operator has just changed is the common path, and
 * splitting them would hide the state machine behind a second navigation.
 *
 * Two ways to edit: the form (the default, until it gets its overhaul) or the
 * whole configuration as JSON. A JSON edit to a PUBLISHED configuration is
 * confirmed first, because PDGS applies updates to live rows immediately.
 */
export const ConfigurationDetailPage = (): JSX.Element => {
  const { configurationId } = useParams<{ configurationId: string }>();
  const navigate = useNavigate();
  const basePath = useBasePath();
  const { canViewConfigurations, canManageConfigurations } = usePermissions();
  const [isSaved, setIsSaved] = useState(false);
  const [mode, setMode] = useState<EditMode>('form');
  const [pendingLiveEdit, setPendingLiveEdit] =
    useState<PendingLiveEdit | null>(null);
  const [isJsonDirty, setIsJsonDirty] = useState(false);
  // Stable, so the editor's dirty-reporting effect doesn't re-run every render.
  const handleJsonDirtyChange = useCallback(
    (dirty: boolean): void => setIsJsonDirty(dirty),
    [],
  );

  // The permission guard below returns before anything renders, but hooks
  // still run — so the skip has to carry the permission too, or the page
  // fetches a configuration the operator is not allowed to see.
  const { configuration, isLoading, hasError, refetch } = useConfiguration(
    configurationId ?? '',
    { skip: !configurationId || !canViewConfigurations },
  );
  const { actionTypes } = useActionsCatalog();
  const { dataProviders } = useDataProviders();

  const {
    updateConfiguration,
    isSaving,
    error: saveError,
    resetError: resetSaveError,
  } = useUpdateConfiguration({ onSuccess: refetch });

  const {
    pendingTransition,
    selectTransition,
    cancelTransition,
    confirmTransition,
    isSaving: isTransitioning,
    error: transitionError,
  } = useConfigurationStatusTransition({ onSuccess: refetch });

  const configurationsPath = `${basePath}/${PARTNER_DATA_GATEWAY_ROUTES.configurations}`;

  const liveJson = useMemo(
    () =>
      configuration
        ? stringifyJson({ ...toConfigurationFile(configuration) })
        : '',
    [configuration],
  );

  const cancelLiveEdit = (): void => {
    setPendingLiveEdit(null);
    // The failure belonged to the attempt just abandoned; don't leave it on
    // the editor underneath.
    resetSaveError();
  };

  /** Resolves to whether the save succeeded; failures surface via `saveError`. */
  const save = async (values: ConfigurationFormValues): Promise<boolean> => {
    if (!configuration) {
      return false;
    }

    setIsSaved(false);

    try {
      await updateConfiguration(configuration.id, values);
      setIsSaved(true);

      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (
    values: ConfigurationFormValues,
  ): Promise<void> => {
    await save(values);
  };

  const handleJsonSubmit = (
    values: CreateConfigurationInput,
    changedFields: (keyof CreateConfigurationInput)[],
  ): void => {
    if (configuration?.status === ConfigurationStatus.Published) {
      setPendingLiveEdit({ values, changedFields, attempted: false });

      return;
    }

    save(values);
  };

  const confirmLiveEdit = async (pending: PendingLiveEdit): Promise<void> => {
    setPendingLiveEdit({ ...pending, attempted: true });

    // Keep the dialog open on failure so the error shows where the
    // operator is looking.
    if (await save(pending.values)) {
      setPendingLiveEdit(null);
    }
  };

  if (!canViewConfigurations) {
    return (
      <HHAlert hhVariant="outlined" severity="warning">
        <span data-testid={CONFIGURATION_DETAIL_NO_ACCESS_TEST_ID}>
          You do not have access to view partner data configurations.
        </span>
      </HHAlert>
    );
  }

  const isDeleted = configuration?.status === ConfigurationStatus.Deleted;
  const isEditable = canManageConfigurations && !isDeleted;

  return (
    <HHStack
      flex={1}
      direction="column"
      gap={4}
      paddingX={6}
      paddingY={6}
      data-testid={CONFIGURATION_DETAIL_PAGE_TEST_ID}
    >
      <BackLink
        to={configurationsPath}
        label={CONFIGURATIONS_BACK_LINK_LABEL}
        testId={CONFIGURATION_DETAIL_BACK_LINK_TEST_ID}
      />

      {isLoading && (
        <SurfaceCard
          padding={6}
          data-testid={CONFIGURATION_DETAIL_LOADING_TEST_ID}
        >
          <HHStack gap={2}>
            <Skeleton variant="text" width="50%" height={36} />
            <Skeleton variant="rounded" width={96} height={24} />
            <Skeleton variant="text" width="35%" />
          </HHStack>
        </SurfaceCard>
      )}

      {!isLoading && !configuration && (
        <HHBox data-testid={CONFIGURATION_DETAIL_ERROR_TEST_ID}>
          <HHAlert hhVariant="outlined" severity="error">
            {hasError || !configurationId
              ? 'Unable to load this configuration. Please try again.'
              : 'This configuration no longer exists.'}
          </HHAlert>
        </HHBox>
      )}

      {configuration && (
        <>
          <SurfaceCard padding={6}>
            <ConfigurationHeader configuration={configuration} />

            {canManageConfigurations && (
              <HHStack gap={1} marginTop={5}>
                <HHTypography
                  hhVariant="variant-bypass"
                  variant="h6"
                  component="h2"
                  sx={{ fontSize: 15, fontWeight: 600 }}
                >
                  Status
                </HHTypography>
                <ConfigurationStatusActions
                  status={configuration.status}
                  disabled={isTransitioning}
                  onSelect={(target): void =>
                    selectTransition(configuration, target)
                  }
                />
                {isDeleted && (
                  <HHTypography
                    hhVariant="variant-bypass"
                    variant="body2"
                    color="text.secondary"
                    data-testid={CONFIGURATION_DETAIL_TERMINAL_TEST_ID}
                  >
                    {CONFIGURATION_DETAIL_TERMINAL_MESSAGE}
                  </HHTypography>
                )}
              </HHStack>
            )}

            {!canManageConfigurations && (
              <HHBox marginTop={5}>
                <HHTypography
                  hhVariant="variant-bypass"
                  variant="body2"
                  color="text.secondary"
                  data-testid={CONFIGURATION_DETAIL_READ_ONLY_TEST_ID}
                >
                  {CONFIGURATION_DETAIL_READ_ONLY_MESSAGE}
                </HHTypography>
              </HHBox>
            )}
          </SurfaceCard>

          {isSaved && (
            <HHAlert
              hhVariant="variant-bypass"
              variant="outlined"
              severity="success"
              data-testid={CONFIGURATION_DETAIL_SAVED_TEST_ID}
            >
              Changes saved.
            </HHAlert>
          )}

          <HHStack direction="row" alignItems="center" gap={2}>
            <HHToggleButtonGroup
              size="small"
              aria-label={CONFIGURATION_DETAIL_MODE_LABEL}
              data-testid={CONFIGURATION_DETAIL_MODE_TEST_ID}
            >
              <HHToggleButton
                hhVariant="variant-bypass"
                value="form"
                selected={mode === 'form'}
                // Unsaved JSON would be lost on the switch (the editor
                // unmounts), so it has to be saved or discarded first.
                disabled={isSaving || (mode === 'json' && isJsonDirty)}
                onClick={(): void => setMode('form')}
                sx={{ borderRadius: '4px 0 0 4px', textTransform: 'none' }}
              >
                {CONFIGURATION_DETAIL_MODE_FORM_LABEL}
              </HHToggleButton>
              <HHToggleButton
                hhVariant="variant-bypass"
                value="json"
                selected={mode === 'json'}
                disabled={isSaving}
                onClick={(): void => setMode('json')}
                sx={{ borderRadius: '0 4px 4px 0', textTransform: 'none' }}
              >
                {CONFIGURATION_DETAIL_MODE_JSON_LABEL}
              </HHToggleButton>
            </HHToggleButtonGroup>
            {mode === 'json' && isJsonDirty && (
              <HHTypography
                hhVariant="variant-bypass"
                variant="body2"
                color="text.secondary"
                data-testid={CONFIGURATION_DETAIL_UNSAVED_JSON_TEST_ID}
              >
                {CONFIGURATION_DETAIL_UNSAVED_JSON_MESSAGE}
              </HHTypography>
            )}
          </HHStack>

          <SurfaceCard padding={6}>
            {mode === 'form' ? (
              <ConfigurationForm
                key={configuration.id}
                configuration={configuration}
                dataProviders={dataProviders}
                actionTypes={actionTypes}
                isSaving={isSaving}
                error={saveError}
                disabled={!isEditable}
                submitLabel="Save changes"
                onCancel={(): void => navigate(configurationsPath)}
                onSubmit={(values): void => {
                  handleSubmit(values);
                }}
              />
            ) : (
              <ConfigurationJsonEditForm
                // Re-seed from the stored row after every successful save.
                key={`${configuration.id}-${configuration.updatedAt}`}
                configuration={configuration}
                isSaving={isSaving}
                error={pendingLiveEdit ? null : saveError}
                readOnly={!isEditable}
                onCancel={(): void => navigate(configurationsPath)}
                onSubmit={handleJsonSubmit}
                onDirtyChange={handleJsonDirtyChange}
              />
            )}
          </SurfaceCard>
        </>
      )}

      {pendingLiveEdit && configuration && (
        <ConfigurationLiveEditConfirmationDialog
          open
          configurationName={configuration.name}
          changedFields={pendingLiveEdit.changedFields}
          liveJson={liveJson}
          loading={isSaving}
          error={pendingLiveEdit.attempted ? saveError : null}
          onConfirm={(): void => {
            confirmLiveEdit(pendingLiveEdit);
          }}
          onCancel={cancelLiveEdit}
        />
      )}

      {pendingTransition && (
        <ConfigurationStatusConfirmationDialog
          open
          targetStatus={pendingTransition.target}
          configurationName={pendingTransition.configuration.name}
          loading={isTransitioning}
          error={transitionError}
          onConfirm={(): void => {
            confirmTransition();
          }}
          onCancel={cancelTransition}
        />
      )}
    </HHStack>
  );
};

export default ConfigurationDetailPage;
